// app.telemetry — product analytics, the platform's way.
//
// (Named `telemetry`, not `analytics`: `app.analytics` is the DuckDB view
// engine. Two different jobs, two different names.)
//
// No Google Analytics, no Segment, no third-party script. An event is a small
// JSON object written straight to the project's own bucket:
//
//   .s3p/analytics/{YYYY-MM-DD}/{ulid}.json
//
// …by the browser, with credentials AWS itself scoped. Signed-in users write
// with their own session; anonymous visitors write with the Identity Pool's
// UNAUTHENTICATED (visitor) role — which is granted write on this one prefix
// and nothing else. Same model as everything else here: IAM is the gate.
//
// WHY THIS MATTERS BEYOND PURITY: your users' traffic never leaves your AWS
// account. There is no cookie, no cross-site identifier, and no vendor to sign
// a DPA with. The data is plain JSON in a bucket you own — query it with the
// analytics view, or just `aws s3 cp`.
//
// WHAT WE DELIBERATELY DON'T COLLECT: no IPs, no user agents, no cookies, no
// cross-session identifier. A signed-in event carries the user's `sub` (you
// already know who they are); an anonymous one carries nothing that identifies
// a person. Respects Do-Not-Track and an explicit opt-out.

import { PutObjectCommand, type S3Client } from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";
import { ulid } from "@s3p/s3db";

export const ANALYTICS_PREFIX = ".s3p/analytics/";

export interface AnalyticsEvent {
  /** Event name, e.g. "page_view", "prompt_copied", "signup_completed". */
  event: string;
  /** Arbitrary small properties. Keep them non-identifying. */
  props?: Record<string, string | number | boolean | null>;
  /** ISO timestamp (set for you). */
  at?: string;
  /** The signed-in user's sub, when there is one. Absent for visitors. */
  sub?: string;
  /** Page path, when in a browser. */
  path?: string;
}

export interface TelemetryModule {
  /** Fire and forget. NEVER throws and never blocks the caller: analytics must
   *  not be able to break the product it is measuring. */
  track(event: string, props?: AnalyticsEvent["props"]): Promise<void>;
  /** Convenience: a page view for the current location. */
  pageView(path?: string): Promise<void>;
  /** True when tracking is off (opt-out or Do-Not-Track). */
  readonly disabled: boolean;
}

export interface TelemetryConfig {
  bucket: string;
  /** Authenticated S3 client (when signed in). */
  getS3Client: () => Promise<S3Client>;
  /** Visitor (unauthenticated) S3 client — used when nobody is signed in. */
  getVisitorS3Client?: () => Promise<S3Client>;
  isSignedIn: () => boolean;
  getSub: () => string | null;
  /** Hard off-switch (config or env). */
  disabled?: boolean;
}

/** Respect the user's stated preference. This is not negotiable: a platform
 *  that ignores Do-Not-Track has no business lecturing anyone about privacy. */
function optedOut(): boolean {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as unknown as { doNotTrack?: string; globalPrivacyControl?: boolean };
  if (nav.doNotTrack === "1" || nav.globalPrivacyControl === true) return true;
  try {
    return localStorage.getItem("s3p.analytics") === "off";
  } catch {
    return false;
  }
}

export function createTelemetry(cfg: TelemetryConfig): TelemetryModule {
  const disabled = Boolean(cfg.disabled) || optedOut();

  async function write(e: AnalyticsEvent): Promise<void> {
    const day = (e.at ?? new Date().toISOString()).slice(0, 10);
    const key = `${ANALYTICS_PREFIX}${day}/${ulid()}.json`;
    // Signed-in → the user's own credentials. Anonymous → the visitor role,
    // which can PUT here and do nothing else.
    const s3 = cfg.isSignedIn()
      ? await cfg.getS3Client()
      : cfg.getVisitorS3Client
        ? await cfg.getVisitorS3Client()
        : null;
    if (!s3) return;
    await s3.send(
      new PutObjectCommand({
        Bucket: cfg.bucket,
        Key: key,
        Body: JSON.stringify(e),
        ContentType: "application/json",
      }),
    );
  }

  return {
    disabled,
    async track(event, props) {
      if (disabled) return;
      const e: AnalyticsEvent = {
        event,
        props,
        at: new Date().toISOString(),
        ...(cfg.getSub() ? { sub: cfg.getSub() as string } : {}),
        ...(typeof window !== "undefined" ? { path: window.location.pathname } : {}),
      };
      // Swallow everything. An analytics failure is not the user's problem, and
      // a tracker that can throw into your render path is a liability.
      try {
        await write(e);
      } catch {
        /* deliberately ignored */
      }
    },
    async pageView(path) {
      return this.track("page_view", path ? { path } : undefined);
    },
  };
}
